import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Globe, Github, Package, ArrowRight, Sparkles, ShieldCheck, Upload, X, Rocket } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { devPreview, devPublishUrl, devPublishGithub, devPublishZip, formatApiError } from "@/lib/api";
import Footer from "@/components/Footer";
import { DEV } from "@/constants/testIds";

const CATEGORIES = ["games","business","education","music","health","finance","productivity","photography","social","travel","ai","utilities","shopping","lifestyle","entertainment","security"];

export default function DevPublish() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("url");

  useEffect(() => {
    if (!loading && (!user || !user.is_developer)) {
      // Send them to upgrade
    }
  }, [loading, user]);

  if (loading) return <div className="max-w-6xl mx-auto p-10 mono text-[#00F0FF] animate-pulse">Loading…</div>;

  if (!user) {
    nav("/login");
    return null;
  }

  if (!user.is_developer) {
    return (
      <div className="max-w-2xl mx-auto px-6 pt-16 text-center">
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] mb-2">// creator plan required</div>
        <h1 className="font-heading font-black text-4xl uppercase tracking-tighter">Activate Creator Access</h1>
        <p className="text-white/60 mt-3">Publishing apps requires a <strong className="text-[#00F0FF]">$20/year</strong> subscription (auto-renews).</p>
        <Link to="/profile" className="inline-flex items-center gap-2 bg-[#00F0FF] hover:bg-white text-black font-bold mono text-xs uppercase tracking-widest px-6 py-3 rounded-full mt-6">
          <Rocket className="w-4 h-4" /> Go to Profile
        </Link>
      </div>
    );
  }

  return (
    <div data-testid={DEV.publishRoot}>
      <div className="max-w-5xl mx-auto px-6 pt-10">
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] mb-3">// creator console</div>
        <h1 className="font-heading font-black text-5xl sm:text-6xl uppercase tracking-tighter">Publish an App</h1>
        <p className="text-white/60 mt-2 max-w-xl">Three ways to ship. Paste a URL, upload a ZIP, or point us at your repo. Live in minutes.</p>

        <div className="flex gap-2 mt-8 flex-wrap">
          <Tab id="url" cur={tab} onClick={setTab} icon={<Globe className="w-4 h-4" />} testId={DEV.tabUrl}>Paste URL</Tab>
          <Tab id="zip" cur={tab} onClick={setTab} icon={<Package className="w-4 h-4" />} testId={DEV.tabZip}>Upload ZIP</Tab>
          <Tab id="github" cur={tab} onClick={setTab} icon={<Github className="w-4 h-4" />} testId={DEV.tabGithub}>GitHub Repo</Tab>
        </div>

        <div className="mt-6">
          {tab === "url" && <UrlPublish />}
          {tab === "zip" && <ZipPublish />}
          {tab === "github" && <GithubPublish />}
        </div>

        <div className="mt-10 mb-4 text-center">
          <Link to="/dev/apps" className="mono text-xs uppercase tracking-widest text-white/60 hover:text-[#00F0FF]">
            View your published apps →
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Tab({ id, cur, onClick, icon, children, testId }) {
  const active = cur === id;
  return (
    <button data-testid={testId} onClick={() => onClick(id)}
      className="mono text-xs uppercase tracking-widest px-4 py-2 rounded-full border transition-colors flex items-center gap-2"
      style={{
        borderColor: active ? "#00F0FF" : "rgba(255,255,255,0.1)",
        color: active ? "#00F0FF" : "rgba(255,255,255,0.7)",
        background: active ? "rgba(0,240,255,0.08)" : "transparent",
      }}>
      {icon} {children}
    </button>
  );
}

function ImageUrlFields({ imgs, setImgs }) {
  const field = (key, label, hint) => (
    <label className="block">
      <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">{label}</div>
      <div className="flex gap-2 items-center">
        <input data-testid={`publish-${key}-input`} value={imgs[key]} onChange={(e) => setImgs({ ...imgs, [key]: e.target.value })}
          placeholder={hint}
          className="flex-1 min-w-0 bg-white/[0.04] border border-white/10 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#00F0FF]/60" />
        {imgs[key] && <img data-testid={`publish-${key}-preview`} src={imgs[key]} alt="" className={`${key === "icon_url" ? "w-10" : "w-20"} h-10 object-cover rounded-md ring-1 ring-white/10`} />}
      </div>
    </label>
  );
  return (
    <div className="grid md:grid-cols-2 gap-3">
      {field("icon_url", "Icon URL (optional)", "Square image, e.g. 512×512")}
      {field("banner_url", "Banner URL (optional)", "Wide image, e.g. 1600×900")}
    </div>
  );
}

function UrlPublish() {
  const nav = useNavigate();
  const [url, setUrl] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [category, setCategory] = useState("utilities");
  const [tags, setTags] = useState("");
  const [isFree, setIsFree] = useState(true);
  const [price, setPrice] = useState(0);
  const [imgs, setImgs] = useState({ icon_url: "", banner_url: "" });

  const fetchPreview = async () => {
    if (!url.trim()) return;
    setBusy(true); setPreview(null);
    try {
      const p = await devPreview("url", url.trim());
      setPreview(p);
      toast.success(p.is_pwa ? "PWA manifest detected" : "Fetched — no PWA manifest found, still publishable");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      const r = await devPublishUrl({
        url: url.trim(), category,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        is_free: isFree, price: Number(price) || 0, ...imgs,
      });
      toast.success(`${r.app.name} is live`);
      nav(`/app/${r.app.slug}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="glass rounded-3xl p-6 md:p-8 space-y-5">
      <div className="flex items-start gap-3">
        <Globe className="w-5 h-5 text-[#00F0FF] mt-0.5" />
        <div>
          <div className="font-heading font-bold text-lg">Paste a website URL</div>
          <p className="text-white/60 text-sm">We'll crawl the page, detect the PWA manifest, and pull your icon, screenshots, and description automatically.</p>
        </div>
      </div>

      <div className="flex gap-2">
        <input data-testid={DEV.urlInput} value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder="https://your-pwa.app"
          className="flex-1 bg-white/[0.04] border border-white/10 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#00F0FF]/60" />
        <button data-testid={DEV.urlPreviewBtn} onClick={fetchPreview} disabled={busy || !url.trim()}
          className="mono text-xs uppercase tracking-widest px-5 py-2.5 rounded-lg border border-[#00F0FF]/40 bg-[#00F0FF]/10 text-[#00F0FF] hover:bg-[#00F0FF]/20 disabled:opacity-40 transition-colors flex items-center gap-1">
          <Sparkles className="w-3.5 h-3.5" /> {busy ? "Fetching…" : "Preview"}
        </button>
      </div>

      {preview && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-4">
          <div className="flex items-start gap-4">
            {preview.icon && <img src={preview.icon} alt="" className="w-20 h-20 rounded-2xl ring-1 ring-white/10 object-cover" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="font-heading font-bold text-xl">{preview.name}</div>
                {preview.is_pwa && (
                  <span className="mono text-[10px] uppercase bg-[#39FF14]/15 text-[#39FF14] px-2 py-0.5 rounded-full border border-[#39FF14]/30">
                    <ShieldCheck className="w-3 h-3 inline mr-1" /> PWA
                  </span>
                )}
              </div>
              <p className="text-white/60 text-sm mt-1">{preview.tagline}</p>
              {preview.screenshots?.length > 0 && (
                <div className="flex gap-2 mt-3 overflow-x-auto hide-scrollbar">
                  {preview.screenshots.slice(0, 3).map((s, i) => (
                    <img key={i} src={s} alt="" className="w-28 h-16 object-cover rounded-md ring-1 ring-white/10" />
                  ))}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}

      <ImageUrlFields imgs={imgs} setImgs={setImgs} />

      <FormBlock category={category} setCategory={setCategory} tags={tags} setTags={setTags}
        isFree={isFree} setIsFree={setIsFree} price={price} setPrice={setPrice} />

      <button data-testid={DEV.urlPublishBtn} onClick={publish} disabled={busy || !url.trim() || !preview}
        className="w-full bg-[#00F0FF] hover:bg-white text-black font-bold mono text-xs uppercase tracking-widest py-3 rounded-full disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
        <Rocket className="w-4 h-4" /> {busy ? "Publishing…" : "Publish App"} <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function GithubPublish() {
  const nav = useNavigate();
  const [repo, setRepo] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [category, setCategory] = useState("utilities");
  const [tags, setTags] = useState("");
  const [launchUrl, setLaunchUrl] = useState("");
  const [imgs, setImgs] = useState({ icon_url: "", banner_url: "" });

  const fetchPreview = async () => {
    if (!repo.trim()) return;
    setBusy(true); setPreview(null);
    try {
      const p = await devPreview("github", repo.trim());
      setPreview(p);
      setLaunchUrl(p.homepage || "");
      toast.success(`⭐ ${p.stars.toLocaleString()} stars`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      const r = await devPublishGithub({
        repo_url: repo.trim(), category,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        launch_url: launchUrl, ...imgs,
      });
      toast.success(`${r.app.name} is live`);
      nav(`/app/${r.app.slug}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="glass rounded-3xl p-6 md:p-8 space-y-5">
      <div className="flex items-start gap-3">
        <Github className="w-5 h-5 text-white mt-0.5" />
        <div>
          <div className="font-heading font-bold text-lg">Connect a GitHub repo</div>
          <p className="text-white/60 text-sm">Paste a public repo URL — we'll pull the name, description, stars, topics, and homepage.</p>
        </div>
      </div>

      <div className="flex gap-2">
        <input data-testid={DEV.githubInput} value={repo} onChange={(e) => setRepo(e.target.value)}
          placeholder="https://github.com/owner/repo"
          className="flex-1 bg-white/[0.04] border border-white/10 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#00F0FF]/60" />
        <button data-testid={DEV.githubPreviewBtn} onClick={fetchPreview} disabled={busy || !repo.trim()}
          className="mono text-xs uppercase tracking-widest px-5 py-2.5 rounded-lg border border-[#00F0FF]/40 bg-[#00F0FF]/10 text-[#00F0FF] hover:bg-[#00F0FF]/20 disabled:opacity-40 transition-colors">
          {busy ? "Fetching…" : "Fetch"}
        </button>
      </div>

      {preview && (
        <div className="glass rounded-2xl p-4 flex items-start gap-4">
          {preview.icon && <img src={preview.icon} alt="" className="w-16 h-16 rounded-2xl ring-1 ring-white/10" />}
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-lg">{preview.owner}/{preview.repo}</div>
            <p className="text-white/60 text-sm mt-1">{preview.tagline || "No description"}</p>
            <div className="flex flex-wrap gap-2 mt-2 mono text-[10px] uppercase text-white/60">
              <span>⭐ {preview.stars.toLocaleString()}</span>
              {preview.language && <span className="text-[#00F0FF]">· {preview.language}</span>}
              {preview.license && <span>· {preview.license}</span>}
            </div>
          </div>
        </div>
      )}

      {preview && (
        <label className="block">
          <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Launch URL (optional)</div>
          <input value={launchUrl} onChange={(e) => setLaunchUrl(e.target.value)}
            placeholder="Where should users open the app? Defaults to homepage/repo."
            className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#00F0FF]/60" />
        </label>
      )}

      <ImageUrlFields imgs={imgs} setImgs={setImgs} />

      <FormBlock category={category} setCategory={setCategory} tags={tags} setTags={setTags} priceHidden />

      <button data-testid={DEV.githubPublishBtn} onClick={publish} disabled={busy || !repo.trim() || !preview}
        className="w-full bg-[#00F0FF] hover:bg-white text-black font-bold mono text-xs uppercase tracking-widest py-3 rounded-full disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
        <Rocket className="w-4 h-4" /> {busy ? "Publishing…" : "Publish App"} <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function ZipPublish() {
  const nav = useNavigate();
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [tagline, setTagline] = useState("");
  const [launchUrl, setLaunchUrl] = useState("");
  const [category, setCategory] = useState("utilities");
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);
  const [imgs, setImgs] = useState({ icon_url: "", banner_url: "" });

  const handleFile = (f) => {
    if (!f) return;
    if (f.size > 100 * 1024 * 1024) { toast.error("ZIP too large (max 100MB)"); return; }
    setFile(f);
    if (!name) setName(f.name.replace(/\.zip$/i, "").replace(/[-_]/g, " "));
  };

  const publish = async () => {
    if (!file || !name) { toast.error("Add a file + name"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", name);
      fd.append("tagline", tagline);
      fd.append("launch_url", launchUrl);
      fd.append("category", category);
      fd.append("tags", tags);
      fd.append("icon_url", imgs.icon_url);
      fd.append("banner_url", imgs.banner_url);
      const r = await devPublishZip(fd);
      toast.success(`${r.app.name} is live`);
      nav(`/app/${r.app.slug}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Publish failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="glass rounded-3xl p-6 md:p-8 space-y-5">
      <div className="flex items-start gap-3">
        <Package className="w-5 h-5 text-[#39FF14] mt-0.5" />
        <div>
          <div className="font-heading font-bold text-lg">Upload a PWA ZIP</div>
          <p className="text-white/60 text-sm">ZIP containing your HTML/CSS/JS + manifest.json. We'll auto-detect metadata and host the bundle.</p>
        </div>
      </div>

      <label className="block border-2 border-dashed border-white/15 rounded-2xl p-8 text-center hover:border-[#39FF14]/50 transition-colors cursor-pointer"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}>
        <input data-testid={DEV.zipInput} type="file" accept=".zip" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
        <Upload className="w-10 h-10 text-white/40 mx-auto mb-3" />
        <div className="font-heading font-bold">{file ? file.name : "Drop your ZIP here"}</div>
        <div className="mono text-[10px] text-white/40 mt-1">or click to browse · max 100MB</div>
        {file && (
          <div className="mono text-[10px] text-[#39FF14] mt-2 inline-flex items-center gap-2">
            <ShieldCheck className="w-3 h-3" /> {(file.size / 1024 / 1024).toFixed(2)} MB · ready
            <button type="button" onClick={(e) => { e.preventDefault(); setFile(null); }} className="text-[#FF003C] hover:text-white ml-2">
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
      </label>

      <div className="grid md:grid-cols-2 gap-3">
        <label className="block">
          <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">App Name *</div>
          <input value={name} onChange={(e) => setName(e.target.value)}
            className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#39FF14]/60" />
        </label>
        <label className="block">
          <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Launch URL (optional)</div>
          <input value={launchUrl} onChange={(e) => setLaunchUrl(e.target.value)}
            placeholder="https://your-app.example"
            className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#39FF14]/60" />
        </label>
      </div>

      <label className="block">
        <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Tagline</div>
        <input value={tagline} onChange={(e) => setTagline(e.target.value)} maxLength={140}
          placeholder="One killer line about your app"
          className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#39FF14]/60" />
      </label>

      <ImageUrlFields imgs={imgs} setImgs={setImgs} />

      <FormBlock category={category} setCategory={setCategory} tags={tags} setTags={setTags} priceHidden accent="#39FF14" />

      <button data-testid={DEV.zipPublishBtn} onClick={publish} disabled={busy || !file || !name}
        className="w-full bg-[#39FF14] hover:bg-white text-black font-bold mono text-xs uppercase tracking-widest py-3 rounded-full disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
        <Rocket className="w-4 h-4" /> {busy ? "Uploading…" : "Publish App"} <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function FormBlock({ category, setCategory, tags, setTags, isFree, setIsFree, price, setPrice, priceHidden, accent = "#00F0FF" }) {
  return (
    <div className="grid md:grid-cols-2 gap-3">
      <label className="block">
        <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Category</div>
        <select data-testid={DEV.category} value={category} onChange={(e) => setCategory(e.target.value)}
          className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-2.5 text-sm focus:outline-none capitalize">
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </label>
      <label className="block">
        <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Tags (comma-separated)</div>
        <input data-testid={DEV.tags} value={tags} onChange={(e) => setTags(e.target.value)}
          placeholder="pwa, indie, dark"
          className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-2.5 text-sm focus:outline-none" />
      </label>
      {!priceHidden && (
        <div className="md:col-span-2 flex items-center gap-4">
          <label className="flex items-center gap-2 mono text-xs uppercase">
            <input type="checkbox" checked={isFree} onChange={(e) => setIsFree(e.target.checked)} className="accent-[#00F0FF]" />
            Free
          </label>
          {!isFree && (
            <label className="flex items-center gap-2 mono text-xs">
              <span className="uppercase text-white/50">Price $</span>
              <input type="number" min="0.99" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)}
                className="w-24 bg-white/[0.04] border border-white/10 rounded-lg px-3 py-1.5 text-sm" />
            </label>
          )}
        </div>
      )}
    </div>
  );
}
